import { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  FiPlus,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import ApiServices from "../../services/apiservices";
import { toast } from "sonner";
import CustomSpinner from "../../components/customspinner";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [showFormModal, setShowFormModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [selectedJob, setSelectedJob] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 5;

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const res = await ApiServices.getRequest("/jobs/fetch.php");

      if (res.success) {
        setJobs(res.jobs || []);
      }
    } catch (err) {
      console.error(err);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // ================= FILTER =================
  const filteredJobs = jobs.filter((job) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      job.title?.toLowerCase().includes(searchLower) ||
      job.location?.toLowerCase().includes(searchLower) ||
      job.team?.toLowerCase().includes(searchLower)
    );
  });

  // ================= PAGINATION =================
  const indexOfLast = currentPage * jobsPerPage;
  const currentJobs = filteredJobs.slice(
    indexOfLast - jobsPerPage,
    indexOfLast
  );
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // ================= VALIDATION =================
  const validationSchema = Yup.object({
    title: Yup.string().required("Required"),
    link: Yup.string(),
    description: Yup.string().required("Required"),
    location: Yup.string().required("Required"),
    team: Yup.string().required("Required"),
    jobType: Yup.string().required("Required"),
    status: Yup.string(),
    qualifications: Yup.string(),
    responsibilities: Yup.string(),
    howToApply: Yup.string(),
    otherDetails: Yup.string(),
    applicationDeadline: Yup.string(),
  });

  // ================= CREATE / EDIT =================
  const handleCreateOrEdit = async (values, { setSubmitting, resetForm }) => {
    try {
      if (isEditMode) {
        const payload = { ...values };

        const res = await ApiServices.putRequest(
          `/jobs/update.php?id=${selectedJob.id}`,
          payload
        );
         if(!res.success){
              toast.error(res.message)
          }else{
              toast.success(res.message);
          }
          
      } else {
        console.log("values::",values);
        const res = await ApiServices.postRequest("/jobs/create.php", values);
         if(!res.success){
            toast.error(res.message)
         }else{
            toast.success(res.message);
          }
      }

      resetForm();
      setShowFormModal(false);
      fetchJobs();
    } catch (err) {
      console.error(err);
      toast.error("Operation failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ================= TOGGLE STATUS =================
  const handleToggleStatus = async () => {
    try {
      const res = await ApiServices.putRequest(
        `/jobs/${selectedJob.id}/toggle-status`
      );

      toast.success(res.message);

      setShowStatusModal(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await ApiServices.deleteRequest(`/jobs/delete.php?id=${selectedJob.id}`);
      toast.success(res.message || "Job deleted successfully");
      setShowDeleteModal(false);
      setSelectedJob(null);
      fetchJobs();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete job");
    }
  };

  const openEdit = (job) => {
    setSelectedJob(job);
    setIsEditMode(true);
    setShowFormModal(true);
  };

  const openCreate = () => {
    setSelectedJob(null);
    setIsEditMode(false);
    setShowFormModal(true);
  };

  const openStatusModal = (job) => {
    setSelectedJob(job);
    setShowStatusModal(true);
  };

  const openDeleteModal = (job) => {
    setSelectedJob(job);
    setShowDeleteModal(true);
  };

  const inputStyle =
  "w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm";

  return (
    <>
    {loading ? <CustomSpinner/> : 
     <main className="flex-1 bg-gray-50 p-6 min-h-screen">
      {/* HEADER */}
      <div className="flex justify-between mb-6">
        <h2 className="text-2xl font-bold">Manage Jobs</h2>
        <button
          onClick={openCreate}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg flex gap-2"
        >
          <FiPlus /> Create Job
        </button>
      </div>

      {/* SEARCH */}
      <input
        placeholder="Search jobs..."
        className="mb-4 p-2 border rounded w-full max-w-md"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />

      {/* TABLE */}
      <div className="bg-white rounded shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-4 text-left">Title</th>
              <th className="text-left">Location</th>
              <th className="text-left">Team</th>
              <th className="text-left">Author</th>
              <th className="text-left">Status</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {currentJobs.length > 0 ? (
            currentJobs.map((job) => (
              <tr key={job.id} className="border-t">
                <td className="p-4 font-medium">{job.title}</td>
                <td>{job.location}</td>
                <td>{job.team}</td>
                <td>{job.author}</td>

                {/* STATUS BADGE */}
                <td>
                  <span
                    className={`px-2 py-1 text-xs rounded ${
                      job.status === "Open"
                        ? " bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                    }`}
                  >
                    {job.status }
                  </span>
                </td>

                <td className="flex gap-3 p-4 justify-end">
                  <button onClick={() => openEdit(job)} title="Edit">
                    <FiEdit2 className="text-blue-600" />
                  </button>

                  {/* <button
                    onClick={() => openStatusModal(job)}
                    title={job.deactivate ? "Activate" : "Deactivate"}
                  >
                    <div className={`w-3 h-3 rounded-full ${job.deactivate ? "bg-red-500" : "bg-green-500"}`}></div>
                  </button> */}

                  <button onClick={() => openDeleteModal(job)} title="Delete">
                    <FiTrash2 className="text-red-600" />
                  </button>
                </td>
              </tr>
            )) ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                    No jobs found matching your search.
                  </td>
                </tr>
              )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="flex justify-between items-center mt-6">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
          className="px-3 py-1 border rounded disabled:opacity-40 bg-white"
        >
          <FiChevronLeft />
        </button>

        <span className="text-sm">
          Page {currentPage} of {totalPages || 1}
        </span>

        <button
          disabled={currentPage === totalPages || totalPages === 0}
          onClick={() => setCurrentPage((p) => p + 1)}
          className="px-3 py-1 border rounded disabled:opacity-40 bg-white"
        >
          <FiChevronRight />
        </button>
      </div>


      {/* CREATE / EDIT MODAL */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">
                {isEditMode ? "Edit Job" : "Create Job"}
              </h3>
              <button onClick={() => setShowFormModal(false)}>
                <FiX />
              </button>
            </div>

            <Formik
              initialValues={{
                title: selectedJob?.title || "",
                link: selectedJob?.link || "",
                description: selectedJob?.description || "",
                location: selectedJob?.location || "",
                team: selectedJob?.team || "",
                jobType: selectedJob?.jobType || "Full-time",
                status: selectedJob?.status || "Open",
                qualifications: selectedJob?.qualifications || "",
                responsibilities: selectedJob?.responsibilities || "",
                howToApply: selectedJob?.howToApply || "",
                otherDetails: selectedJob?.otherDetails || "",
                applicationDeadline: selectedJob?.applicationDeadline || "",
              }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleCreateOrEdit}
            >
              {({ isSubmitting }) => (
                <Form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                    <Field name="title" placeholder="e.g. Senior Software Engineer" className={inputStyle} />
                    <ErrorMessage name="title" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Link (LinkedIn, etc)</label>
                    <Field name="link" placeholder="https://..." className={inputStyle} />
                    <ErrorMessage name="link" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                    <Field name="location" placeholder="e.g. Benin City, Edo State" className={inputStyle} />
                    <ErrorMessage name="location" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Team / Department</label>
                    <Field name="team" placeholder="e.g. Engineering" className={inputStyle} />
                    <ErrorMessage name="team" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Job Type</label>
                    <Field as="select" name="jobType" className={inputStyle}>
                      <option value="">Select Job Type...</option>
                      <option value="Full-time">Full-time</option>
                      <option value="Part-time">Part-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                      <option value="Remote">Remote</option>
                      <option value="Temporary">Temporary</option>
                    </Field>
                    <ErrorMessage name="jobType" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

               
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <Field as="select" name="status" className={inputStyle}>
                      <option value="Open">Open</option>
                      <option value="Closed">Closed</option>
                    </Field>
                    <ErrorMessage name="status" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (Overview)</label>
                    <Field as="textarea" rows="4" name="description" placeholder="Job Overview..." className={inputStyle} />
                    <ErrorMessage name="description" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Responsibilities</label>
                    <Field as="textarea" rows="4" name="responsibilities" placeholder="Enter each responsibility on a new line..." className={inputStyle} />
                    <ErrorMessage name="responsibilities" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Qualifications</label>
                    <Field as="textarea" rows="4" name="qualifications" placeholder="Enter each qualification on a new line..." className={inputStyle} />
                    <ErrorMessage name="qualifications" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">How to Apply</label>
                    <Field as="textarea" rows="4" name="howToApply" placeholder="Enter instructions on new lines..." className={inputStyle} />
                    <ErrorMessage name="howToApply" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Other Details</label>
                    <Field as="textarea" rows="4" name="otherDetails" placeholder="Enter each detail on a new line..." className={inputStyle} />
                    <ErrorMessage name="otherDetails" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Application Deadline</label>
                    <Field name="applicationDeadline" type="date" className={inputStyle} />
                    <ErrorMessage name="applicationDeadline" component="div" className="text-red-500 text-xs mt-1" />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 mt-4"
                  >
                    {isSubmitting
                      ? "Saving..."
                      : isEditMode
                      ? "Update Job"
                      : "Create Job"}
                  </button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* STATUS MODAL */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded shadow w-80 text-center">
            <h3 className="text-lg font-semibold mb-3">
              {selectedJob?.deactivate ? "Activate Job?" : "Deactivate Job?"}
            </h3>

            <p className="mb-6">
              Are you sure you want to {selectedJob?.deactivate ? "activate" : "deactivate"} <strong>{selectedJob?.title}</strong>?
            </p>

            <div className="flex gap-3">
              <button onClick={() => setShowStatusModal(false)} className="flex-1 border py-2 rounded hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleToggleStatus}
                className={`flex-1 text-white py-2 rounded ${selectedJob?.deactivate ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
              >
                {selectedJob?.deactivate ? "Activate" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded shadow w-80 text-center">
            <h3 className="text-lg font-semibold mb-3 text-red-600">Delete Job</h3>
            <p className="mb-6">
              Are you sure you want to permanently delete <strong>{selectedJob?.title}</strong>? This action cannot be undone.
            </p>

            <div className="flex gap-3">
              <button onClick={() => setShowDeleteModal(false)} className="flex-1 border py-2 rounded hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleDelete} className="flex-1 text-white py-2 rounded bg-red-600 hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </main>}
    </>
  );
}